import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowRight } from 'lucide-react'
import MegaPeekCard from './MegaPeekCard'

interface MegaServicesProps {
  lang: string
  onItemClick: () => void
}

// `slug` builds the default `/services/<slug>` path. `path` overrides it with a
// route outside /services (e.g. Hårtab → the trichology landing page).
interface MegaServiceItem {
  slug: string
  key: string
  path?: string
}

const GROUPS: Array<{ titleKey: string; items: MegaServiceItem[] }> = [
  {
    titleKey: 'megaServices.groupCuts',
    items: [
      { slug: 'womens-haircut', key: 'womensCut' },
      { slug: 'mens-haircut', key: 'mensCut' },
      { slug: 'childrens-haircut', key: 'childrensCut' },
    ],
  },
  {
    titleKey: 'megaServices.groupColour',
    items: [
      { slug: 'colour-treatments', key: 'colour' },
      { slug: 'toning-glossing', key: 'toning' },
      { slug: 'highlights-foils', key: 'highlights' },
      { slug: 'balayage-babylights', key: 'balayage' },
      { slug: 'blonde-specialist', key: 'blonde' },
    ],
  },
  {
    titleKey: 'megaServices.groupCare',
    items: [
      { slug: 'keratin-structure', key: 'keratin' },
      { slug: 'hair-care-repair', key: 'repair' },
      { slug: 'wash-blowdry', key: 'wash' },
      // Hair loss links to the trichology landing page, not a /services page.
      { slug: 'hair-loss', key: 'hairLoss', path: '/trichology' },
    ],
  },
  {
    titleKey: 'megaServices.groupSpecialty',
    items: [
      { slug: 'permanent-waves', key: 'permanent' },
      { slug: 'extensions', key: 'extensions' },
      { slug: 'hair-systems', key: 'systems' },
      { slug: 'brows-lashes', key: 'brows' },
      { slug: 'updos-makeup', key: 'updos' },
    ],
  },
]

export default function MegaServices({ lang, onItemClick }: MegaServicesProps) {
  const { t } = useTranslation()
  return (
    <div className="flex gap-8">
      <div className="flex-1 min-w-0">
        <div className="grid grid-cols-2 sm:grid-cols-[auto_auto_auto_auto] gap-8">
          {GROUPS.map((group) => (
            <div key={group.titleKey}>
              <h3 className="font-body font-semibold text-[10px] tracking-[0.25em] text-gold uppercase mb-4 pb-2 border-b border-gold/20">
                {t(group.titleKey)}
              </h3>
              <ul className="space-y-2.5">
                {group.items.map((item) => (
                  <li key={item.slug}>
                    <Link
                      to={`/${lang}${item.path ?? `/services/${item.slug}`}`}
                      onClick={onItemClick}
                      className="block font-body text-sm text-mocha/80 hover:text-gold transition-colors"
                    >
                      {t(`megaServices.items.${item.key}`)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-3">
          <Link
            to={`/${lang}/services`}
            onClick={onItemClick}
            className="inline-flex items-center gap-1 font-body text-[11px] tracking-[0.18em] uppercase text-gold hover:text-mocha transition-colors"
          >
            {t('megaServices.viewAll')} <ArrowRight size={11} />
          </Link>
        </div>
      </div>

      {/* Featured column — Hair Length Guide + Cancellation Policy. Mirrors
          MegaTrichology's two-card pattern so panel heights line up. */}
      <div className="w-96 shrink-0 pl-6 border-l border-ivory-dark">
        <h3 className="font-body font-semibold text-[10px] tracking-[0.25em] text-gold uppercase mb-4 pb-2 border-b border-gold/20">
          {t('megaServices.featured')}
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <MegaPeekCard
            to={`/${lang}/hair-length-guide`}
            src="/images/services/01-womens-haircut-480w.webp"
            onItemClick={onItemClick}
          >
            <p className="mt-3 font-display text-sm text-mocha leading-snug transition-colors duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:text-gold">
              {t('megaServices.featuredHairGuide')}
              <span
                aria-hidden="true"
                className="mt-1.5 block h-px w-full origin-left scale-x-0 bg-gold transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-x-100"
              />
            </p>
          </MegaPeekCard>
          <MegaPeekCard
            to={`/${lang}/cancellation-policy`}
            src="/images/membership/man-back-view.webp"
            onItemClick={onItemClick}
          >
            <p className="mt-3 font-display text-sm text-mocha leading-snug transition-colors duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:text-gold">
              {t('megaServices.featuredCancellation')}
              <span
                aria-hidden="true"
                className="mt-1.5 block h-px w-full origin-left scale-x-0 bg-gold transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-x-100"
              />
            </p>
          </MegaPeekCard>
        </div>
      </div>
    </div>
  )
}
